import { lodestarColors, ursaColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { themed } from "@smartsheet/sds-theme-internal";
import { BaseButtonStyledProps, ButtonAppearances, ButtonRenderState } from "../types";
import { mapButtonAppearance } from "./mapButtonAppearance";

export const linkTextColorTheme = themed({
    enabled: {
        "lodestar-light": lodestarColors.ctaLink, //#0970be no token exists
        "ursa-light": `var(--sds--link--color--rest, ${color("color--text--primary--default", ursaColors.blue600)})`,
    },
    active: {
        "lodestar-light": lodestarColors.focusDark, //#4c649d no token exists
        "ursa-light": `var(--sds--link--color--active, ${ursaColors.linkDark})`,
    },
    disabled: {
        "lodestar-light": color("color--text--neutral--weaker", lodestarColors.neutralDark10),
        "ursa-light": `var(--sds--link--color--disabled, ${color(
            "color--text--neutral--weaker",
            lodestarColors.neutralDark10
        )})`,
    },
    hover: {
        "lodestar-light": lodestarColors.ctaLink,
        "ursa-light": `var(--sds--link--color--hover, ${color("color--text--primary--default", ursaColors.blue600)})`,
    },
});

export const buttonTextColorTheme = themed({
    primary: {
        //only lodestar-light
        enabled: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
        active: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
        disabled: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
    },
    secondary: {
        //only lodestar-light
        enabled: color("color--text--neutral--default", lodestarColors.neutralDark30),
        active: color("color--text--neutral--default", lodestarColors.neutralDark30),
        disabled: color("color--background--neutral--stronger", lodestarColors.neutralAccessible),
    },
    destructive: {
        //only lodestar-light
        enabled: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
        active: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
        disabled: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
    },
    "secondary-destructive": {
        //only lodestar-light
        enabled: lodestarColors.buttonDestructive, //#cc2e3b no token exists
        active: lodestarColors.buttonDestructive, //#cc2e3b no token exists
        disabled: lodestarColors.buttonDestructiveDisabled, //no token exists
    },
    share: {
        //only lodestar-light
        enabled: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
        active: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
        disabled: color("color--text--neutral-inverse--strong", lodestarColors.neutralLight40),
    },
    borderless: {
        enabled: lodestarColors.buttonPrimary, //#3576ba no token exists
        active: lodestarColors.buttonPrimary, //#3576ba no token exists
        disabled: color("color--text--neutral--weaker", lodestarColors.neutralDark10),
    },
    //Below are exclusive to ursa-light
    "primary-filled": {
        enabled: color("color--text--primary--weakest", ursaColors.blue50),
        active: color("color--text--primary--weakest", ursaColors.blue50),
        disabled: color("color--text--primary--weakest", ursaColors.blue25),
    },
    "primary-empty": {
        enabled: color("color--text--primary--default", ursaColors.blue600),
        active: color("color--text--primary--default", ursaColors.blue600),
        disabled: color("color--text--primary--weak", ursaColors.blue300),
    },
    "secondary-filled": {
        enabled: color("color--text--neutral--strong", ursaColors.neutral800),
        active: color("color--text--neutral--strong", ursaColors.neutral800),
        disabled: color("color--text--neutral--weak", ursaColors.neutral500),
    },
    "secondary-outlined": {
        enabled: color("color--text--neutral--strong", ursaColors.neutral800),
        active: color("color--text--neutral--strong", ursaColors.neutral800),
        disabled: color("color--text--neutral--weak", ursaColors.neutral500),
    },
    "secondary-empty": {
        enabled: color("color--text--neutral--strong", ursaColors.neutral800),
        active: color("color--text--neutral--strong", ursaColors.neutral800),
        disabled: color("color--text--neutral--weak", ursaColors.neutral500),
    },
    "canvas-filled": {
        enabled: color("color--text--neutral--strong", ursaColors.slate700),
        active: color("color--text--neutral--strong", ursaColors.slate700),
        disabled: color("color--text--slate--weakest", ursaColors.slate300),
    },
    "canvas-empty": {
        enabled: color("color--text--neutral--strong", ursaColors.slate700),
        active: color("color--text--neutral--strong", ursaColors.slate700),
        disabled: color("color--text--slate--weakest", ursaColors.slate300),
    },
    "destructive-filled": {
        enabled: color("color--text--neutral-inverse--strong", ursaColors.neutral00),
        active: color("color--text--neutral-inverse--strong", ursaColors.neutral00),
        disabled: color("color--text--neutral-inverse--strong", ursaColors.neutral00),
    },
    "destructive-empty": {
        enabled: color("color--text--destructive--default", ursaColors.red500),
        active: color("color--text--destructive--default", ursaColors.red500),
        disabled: color("color--text--destructive--weak", ursaColors.red75),
    },
});

// For mapping state to the fontSettings color object above
const mapButtonState = ({ isDisabled = false, isActive = false }) => {
    // Disabled should always win
    if (isDisabled) {
        return "disabled";
    }
    // Active should win over enabled
    if (isActive) {
        return "active";
    }
    return "enabled";
};

// For mapping state to the fontSettings color object above
const mapLinkState = ({ isHover = false, isDisabled = false, isActive = false }) => {
    // Disabled should always win
    if (isDisabled) {
        return "disabled";
    }
    // Active should win over enabled
    if (isActive) {
        return "active";
    }
    if (isHover) {
        return "hover";
    }
    return "enabled";
};

export const getButtonTextColor = ({
    appearance,
    state,
    themeMode,
}: Pick<BaseButtonStyledProps, "appearance" | "state" | "themeMode">): string => {
    if (appearance === "link") {
        const resolvedState = mapLinkState(state);
        return linkTextColorTheme[resolvedState](themeMode);
    }

    const resolvedState = mapButtonState(state);
    // "link" will not be returned here since we test for it above.  Downcast to ButtonAppearances to avoid lint warning.
    const resolvedAppearance = mapButtonAppearance(appearance, themeMode) as ButtonAppearances;
    return buttonTextColorTheme[resolvedAppearance][resolvedState](themeMode);
};

// For mapping state to the color object above
const mapStateToTextVar = (state: ButtonRenderState) => {
    // Disabled should always win
    if (state.isDisabled) {
        return "--sds--button--color--text--disabled";
    }
    // Active should win over hover
    if (state.isActive) {
        return "--sds--button--color--text--active";
    }
    return "--sds--button--color--text--rest";
};

export const getButtonTextColorVar = (state: ButtonRenderState): string => {
    return `${mapStateToTextVar(state)}`;
};
